import { IconEdit, IconTrash } from '@tabler/icons-react';
import { AppTable, AppTableProps, RemoveModal } from 'components/organisms';
import { appPaths } from 'consts/paths/paths';
import { commonTexts, settingsTexts } from 'consts/text';
import { generatePath } from 'react-router-dom';
import { DietDTO, DietId } from 'types';
import { dietFields } from 'consts/diets/dietFields';
import { useMemo, useState } from 'react';
import { PriceDisplay } from 'components/molecules';
import { useSettingsAbilities } from 'abilities';
import { useNavigateWithState } from 'hooks/useNavigateWithState/useNavigateWithState';
import { parseStringIntoFormattedStringDate } from 'utils/parsers/dateTime/parseStringIntoFormattedStringDate';
import { useDietsPageNetworkManage } from './hooks/useDietsPageNetworkManage';
import { useMealTypesPageNetworkManage } from '../MealTypesPage/hooks/useMealTypesPageNetworkManage';

export const DietsPage = () => {
    const [selectedToRemoveDietId, setSelectedToRemoveDietId] = useState<DietId | null>(null);
    const { statefulNavigate } = useNavigateWithState();
    const { settingsAbilities } = useSettingsAbilities();
    const { checkIfCanManageDietSettings } = settingsAbilities;
    const canManageDietSettings = checkIfCanManageDietSettings();

    const handleRemoveDietClick = (diet: DietDTO) => setSelectedToRemoveDietId(diet.id);
    const onDietRemoveRequestFinish = () => setSelectedToRemoveDietId(null);

    const {
        dietsData,
        isDietsDataInitialLoading,
        isDietsDataRefetching,
        isDietsDataError,
        removeDiet,
        isRemoveDietLoading,
    } = useDietsPageNetworkManage({
        onDietRemoveRequestFinish,
    });

    const { mealTypesData } = useMealTypesPageNetworkManage({
        onMealTypeRemoveRequestFinish: () => {},
    });

    // Generate columns based on predefined meal types
    const columns: AppTableProps<DietDTO>['columns'] = useMemo(() => {
        const baseColumns = [
            {
                title: settingsTexts.diets.columnNames.name,
                dataIndex: dietFields.name,
                key: dietFields.name,
                isVisibleAsExtendableInMobile: false,
            },
        ];

        const dateFromColumn = {
            title: commonTexts.dataLabels.pricesDateFrom,
            dataIndex: 'meals',
            key: 'dateFrom',
            render: (meals: DietDTO['meals']) =>
                meals ? parseStringIntoFormattedStringDate(meals[meals.length - 1].dateFrom) : '-',
            isVisibleAsExtendableInMobile: false,
        };

        const priceColumns = (mealTypesData ?? []).map((mealType) => ({
            title: mealType.name,
            dataIndex: 'meals',
            key: `mealPrice_${mealType.id}`,
            render: (meals: DietDTO['meals']) => {
                // Get current meal prices from the most recent entry
                const currentMealPrices =
                    Array.isArray(meals) && meals.length > 0
                        ? meals[meals.length - 1]?.mealPrices || []
                        : [];
                const mealPrice = currentMealPrices?.find((mp) => mp.mealTypeId === mealType.id);
                return <PriceDisplay value={mealPrice?.price ?? 0} />;
            },
            isVisibleAsExtendableInMobile: true,
        }));

        return [...baseColumns, dateFromColumn, ...priceColumns];
    }, [mealTypesData]);

    const getOptions = useMemo(() => {
        if (!canManageDietSettings) {
            return undefined;
        }

        return [
            {
                label: commonTexts.actionLabels.edit,
                onClick: (diet: DietDTO) =>
                    statefulNavigate(
                        generatePath(appPaths.app.settings.diets.edit.pickedDiet, {
                            dietId: diet.id,
                        }),
                    ),
                Icon: <IconEdit />,
            },
            {
                label: commonTexts.actionLabels.delete,
                onClick: handleRemoveDietClick,
                Icon: <IconTrash />,
            },
        ];
    }, [canManageDietSettings, statefulNavigate]);

    const handleRemoveDietConfirm = () => {
        if (!selectedToRemoveDietId) {
            return;
        }
        removeDiet(selectedToRemoveDietId);
    };

    const navigateToDietAdd = () => statefulNavigate(appPaths.app.settings.diets.add);

    return (
        <>
            <RemoveModal
                open={!!selectedToRemoveDietId}
                title={settingsTexts.diets.removeModalTitle}
                confirmActionLoading={isRemoveDietLoading}
                onOk={handleRemoveDietConfirm}
                onCancel={onDietRemoveRequestFinish}
            />
            <div style={{ marginTop: '16px' }}>
                <AppTable<DietDTO>
                    title={settingsTexts.diets.dietListTitle}
                    addButtonLabel={canManageDietSettings ? settingsTexts.diets.addDiet : undefined}
                    onAddButtonClick={navigateToDietAdd}
                    options={getOptions}
                    columns={columns}
                    dataSource={dietsData || []}
                    isError={isDietsDataError}
                    emptyInfo={settingsTexts.diets.emptyDietList}
                    isDataInitialLoading={isDietsDataInitialLoading}
                    isDataRefetching={isDietsDataRefetching}
                    isOptionsLoading={isRemoveDietLoading}
                    isTableManageUtilsHidden
                    rowKey={({ id }) => id}
                />
            </div>
        </>
    );
};
